// Admin Settings Page
import { useState, useEffect } from 'react';
import adminApi from '../services/adminApi';
import Loading from '../components/Loading';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Load settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const data = await adminApi.getSettings();
        console.log('Admin: Fetched settings:', data);
        setSettings(data);
      } catch (err) {
        console.error('Error fetching admin settings:', err);
        setError('Failed to load settings: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle prompt option changes
  const handlePromptChange = (key, value) => {
    if (!settings) return;
    console.log(settings.promptOptions)
    setSettings({
      ...settings,
      promptOptions: {
        ...settings.promptOptions,
        [key]: value
      }
    });
  };

  // Save settings
  const saveSettings = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage('');
      
      const result = await adminApi.updateSettings(settings);
      console.log('Admin: Settings updated:', result);
      
      setSuccessMessage('Settings saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving admin settings:', err);
      setError('Failed to save settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading admin settings..." />;
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Admin Settings</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {settings && (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900">Model Settings</h3>
            <p className="mt-1 text-sm text-gray-500">
              Configure the AI model used for analyses
            </p>
            
            <div className="mt-6 space-y-6">
              {/* Model Selection */}
              <div>
                <label htmlFor="model" className="block text-sm font-medium text-gray-700">
                  AI Model
                </label>
                <div className="mt-1">
                  <select
                    id="model"
                    name="model"
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={settings.model || 'gemini-2.0-flash'}
                    onChange={(e) => {
                      setSettings({
                        ...settings,
                        model: e.target.value
                      });
                    }}
                  >
                    <option value="gemini-2.5-pro-preview-03-25">Gemini 2.5 Pro</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gpt-4.1">GPT 4.1</option>
                    {/* <option value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</option>
                    <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option> */}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    The AI model to use for analysis. Different models have different capabilities and costs.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900">Analysis Prompts</h3>
            <p className="mt-1 text-sm text-gray-500">
              Customize the prompts used for different types of analysis
            </p>
            
            <div className="mt-6 space-y-6">
              {/* Executive Summary Prompt */}
              <div>
                <label htmlFor="executiveSummary" className="block text-sm font-medium text-gray-700">
                  Executive Summary
                </label>
                <div className="mt-1">
                  <textarea
                    id="executiveSummary"
                    name="executiveSummary"
                    rows={3}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={settings.promptOptions.executiveSummary || ''}
                    onChange={(e) => handlePromptChange('executiveSummary', e.target.value)}
                  />
                </div>
              </div>

              {/* Content Types Prompt */}
              <div>
                <label htmlFor="contentTypes" className="block text-sm font-medium text-gray-700">
                  Content Types
                </label>
                <div className="mt-1">
                  <textarea
                    id="contentTypes"
                    name="contentTypes"
                    rows={3}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={settings.promptOptions.contentTypes || ''}
                    onChange={(e) => handlePromptChange('contentTypes', e.target.value)}
                  />
                </div>
              </div>

              {/* Competitor Analysis Prompt */}
              <div>
                <label htmlFor="competitorAnalysis" className="block text-sm font-medium text-gray-700">
                  Competitor Analysis
                </label>
                <div className="mt-1">
                  <textarea
                    id="competitorAnalysis"
                    name="competitorAnalysis"
                    rows={3}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={settings.promptOptions.competitorAnalysis || ''}
                    onChange={(e) => handlePromptChange('competitorAnalysis', e.target.value)}
                  />
                </div>
              </div>

              {/* Sitemap Prompt */}
              <div>
                <label htmlFor="sitemap" className="block text-sm font-medium text-gray-700">
                  Sitemap
                </label>
                <div className="mt-1">
                  <textarea
                    id="sitemap"
                    name="sitemap"
                    rows={3}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={settings.promptOptions.sitemap || ''}
                    onChange={(e) => handlePromptChange('sitemap', e.target.value)}
                  />
                </div>
              </div>
              
            </div>
          </div>

          <div className="px-4 py-4 sm:px-6 flex justify-end">
            <button
              type="button"
              onClick={saveSettings}
              disabled={isSaving}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white 
                ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;